import { inject, Injectable } from '@angular/core';

import { ClarityDialog } from '../dialog/clarity-dialog.service';
import type { ClarityDialogRef } from '../dialog/clarity-dialog-ref';
import type { ClarityDialogConfig } from '../models/clarity-dialog-config.model';
import { ClarityActionConfirmDialogComponent } from './clarity-action-confirm-dialog.component';
import type { ClarityActionConfirmDialogData } from './clarity-action-confirm-dialog-data.model';

/** Ported from Clarity `apps/web/src/shared/ui/dialog/ConfirmDialog.tsx` (commit `f9217bb5a`). */
/**
 * Opens brain's `ConfirmDialog` — `ClarityActionConfirmDialogComponent` in a `ClarityDialog`, `passive` unless the
 * caller asks for `trap`. Passive is brain's `Modal`: focus stays on the trigger, Escape closes the dialog only while
 * focus is outside it, and Tab is not confined (brain defect #36).
 *
 * The shared confirmation for an action that takes time. `ClarityDialogService.confirm` (`client-ui-clarity-chrome`)
 * closes as soon as it is answered; this one follows brain — it reads `Processing…` while the caller's action runs,
 * cannot be dismissed meanwhile and stays open until the caller closes it, a failed action included. The ref's
 * `closed` is brain's `onClose` — Cancel, Escape and the backdrop all end there with no result;
 * `onConfirm` is the only other way out, and the caller closes the ref itself once its action has settled. It lived in
 * the Domain files panel's library as `ClarityDomainFilesConfirmDialog` while this library had no such confirmation,
 * and moved back unchanged.
 */
@Injectable({ providedIn: 'root' })
export class ClarityActionConfirmDialog {
	private readonly dialog = inject(ClarityDialog);

	/**
	 * `config` takes the four dialog members a confirmation may state — `panelClass`, the hook
	 * `ClarityDialogConfig.panelClass` describes; `injector`, the parent the dialog is opened under;
	 * `closeOnNavigation`, `false` for a confirmation on a screen that survives Back and Forward, as
	 * brain's modal stays while its component is mounted; and `focus`, `passive` by default, `trap` for a
	 * confirmation opened from a surface that already traps focus. Everything else stays the confirmation's
	 * own: it is always named by its title. The body itself reads only the ref and `data`; whatever
	 * `onConfirm` and the signals need they carry with them from the screen that built them.
	 */
	public open(
		data: ClarityActionConfirmDialogData,
		config?: Pick<ClarityDialogConfig, 'panelClass' | 'injector' | 'closeOnNavigation' | 'focus'>,
	): ClarityDialogRef<void> {
		return this.dialog.open<void, ClarityActionConfirmDialogData>(ClarityActionConfirmDialogComponent, {
			data,
			focus: config?.focus ?? 'passive',
			ariaLabel: data.title,
			panelClass: config?.panelClass,
			injector: config?.injector,
			closeOnNavigation: config?.closeOnNavigation,
		});
	}
}
